import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import { type ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

const ReactSelectMemberships = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();

  const [inputValue, setInputValue] = useState('');

  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ['memberships', networkId, inputValue],
    queryFn: () =>
      axiosWithSyntaxHandlers
        .get<{
          data: Array<{
            description: string;
            id: number;
            name: string;
            price: number;
          }>;
        }>(Routes.backoffice_network_memberships_types_path(networkId), {
          params: {
            fulltext: inputValue,
            format: 'json',
            per_page: 25,
          },
        })
        .then((res) => res.data.data),
    staleTime: ms('5m'),
  });

  const onInputChange = useDebouncedCallback((value) => {
    setInputValue(value);
  }, 500);

  const options = useMemo(() => {
    const combinedMemberships = [
      ...(memberships || []),
      ...(Array.isArray(props.value) ? props.value : [props.value]),
    ];
    const uniqueMembershipsMap = new Map();

    combinedMemberships.forEach((membership) => {
      if (membership && typeof membership.id !== 'undefined') {
        uniqueMembershipsMap.set(membership.id, membership);
      }
    });

    return Array.from(uniqueMembershipsMap.values()).map(({ id, name }) => ({
      value: id,
      label: name,
    }));
  }, [memberships, props.value]);

  return (
    <ReactSelectInput
      placeholder={__('Search and select memberships')}
      isMulti
      {...props}
      cacheOptions
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectMemberships;
